/**
 * `/newsletter` — the article-and-booking WhatsApp send.
 *
 * Mario's 2026-09-15 brief asked for a two-button template (article link +
 * booking link) sent to members still mid-flow. The template
 * (`member_article_and_booking`) and its caller (`send_whatsapp_newsletter`)
 * shipped on 2026-09-16 — but the caller was reachable only from the CLI, so
 * there was nothing to press. Mario, the same day: *"close the real gap."*
 *
 * NO BESPOKE ENDPOINT. Everything goes through `dispatchAction` →
 * `POST /api/dispatch/:action` as `human:staff`, so `api: []` in the page
 * registry — the same posture as `/errors` and `/reports`.
 *
 * THE TWO-CALL SHAPE IS THE WHOLE POINT, and the page exists to make it
 * legible rather than to hide it. WhatsApp has no draft: a send is a send, and
 * it cannot be recalled. So `send_whatsapp_newsletter` resolves the cohort and
 * returns the COUNT when called without `confirm_recipient_count`, and sends
 * only when handed that exact number back. This page therefore has two
 * buttons, not one, and the second is deliberately not reachable until the
 * first has been pressed:
 *
 *   Preview  → "47 members would receive this", sends nothing
 *   Send     → passes 47; the action refuses if the cohort has moved since
 *
 * That refusal is a feature. A cohort that changed between looking and sending
 * means the message would reach a different set of people than the one
 * reviewed, and the action rejects `recipient_count_changed` rather than
 * guessing. The page surfaces that plainly and asks for a fresh preview.
 */

import { useCallback, useState } from "react";
import { dispatchAction } from "../lib/dispatch";

interface NewsletterResult {
  readonly status: "preview" | "sent";
  readonly recipient_count: number;
  readonly sent: number;
  readonly failed: number;
  readonly failed_hashes: ReadonlyArray<string>;
  readonly template_name: string;
  readonly article_slug: string;
}

/** Mirrors the action's schema: ONE safe path segment, no separators. */
const SLUG_PATTERN = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/u;

export function Newsletter(): JSX.Element {
  const [slug, setSlug] = useState("");
  const [blurb, setBlurb] = useState("");
  const [previewCount, setPreviewCount] = useState<number | null>(null);
  const [sentResult, setSentResult] = useState<NewsletterResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const slugValid = SLUG_PATTERN.test(slug);
  const blurbValid = blurb.trim().length > 0 && blurb.length <= 500;
  const inputsValid = slugValid && blurbValid;

  const run = useCallback(
    async (confirmCount: number | null) => {
      setBusy(true);
      setError(null);
      const result = await dispatchAction<
        Record<string, unknown>,
        NewsletterResult
      >("send_whatsapp_newsletter", {
        article_slug: slug,
        blurb,
        ...(confirmCount !== null
          ? { confirm_recipient_count: confirmCount }
          : {}),
      });
      setBusy(false);
      if (!result.ok) {
        // The cohort moved between preview and send. Drop the stale count so
        // the only way forward is a fresh preview — the operator must see the
        // new number before committing to it.
        if (result.code === "recipient_count_changed") setPreviewCount(null);
        setError(`${result.code}: ${result.message}`);
        return;
      }
      if (result.response.status === "preview") {
        setPreviewCount(result.response.recipient_count);
        setSentResult(null);
      } else {
        setSentResult(result.response);
        setPreviewCount(null);
      }
    },
    [slug, blurb],
  );

  return (
    <div className="page">
      <h1>Newsletter — WhatsApp</h1>
      <p className="subtle" style={{ maxWidth: 620 }}>
        Sends <code>member_article_and_booking</code> to every member who is
        still mid-flow and reachable on WhatsApp. Two buttons on purpose:
        WhatsApp has no draft, so the preview is the review.
      </p>

      <fieldset>
        <legend>Message</legend>
        <div className="form-row">
          <label htmlFor="nl-slug">Article</label>
          <input
            id="nl-slug"
            type="text"
            value={slug}
            placeholder="new-belgravia-studio-kinnerton-street"
            onChange={(e) => {
              setSlug(e.target.value);
              setSentResult(null);
            }}
          />
          <span className="subtle">
            {" "}
            e-pulsive.co.uk/blog/<strong>{slug || "…"}</strong>
          </span>
        </div>
        {slug.length > 0 && !slugValid && (
          <p className="tag tag-error" style={{ display: "inline-block" }}>
            One path segment only — letters, digits and hyphens. A slash or a
            scheme here would point the button somewhere other than the blog.
          </p>
        )}

        <div className="form-row">
          <label htmlFor="nl-blurb">What the article is</label>
          <textarea
            id="nl-blurb"
            rows={3}
            maxLength={500}
            value={blurb}
            placeholder="how we chose Kinnerton Street for the new studio"
            onChange={(e) => {
              setBlurb(e.target.value);
              setSentResult(null);
            }}
          />
          <span className="subtle">{blurb.length}/500</span>
        </div>

        {inputsValid && (
          <div
            className="subtle"
            style={{
              marginTop: 12,
              padding: 12,
              maxWidth: 560,
              whiteSpace: "pre-wrap",
            }}
          >
            <strong>Preview</strong>
            {"\n\n"}
            Hi <em>&lt;first name&gt;</em>, we&apos;ve put together something we
            think you&apos;ll find useful — {blurb}.{"\n\n"}
            Have a read whenever suits, and book your next session if
            you&apos;d like one.{"\n\n"}[ Read the article ] [ Book a session ]
          </div>
        )}
      </fieldset>

      <div className="form-row" style={{ gap: 12, marginTop: 16 }}>
        <button
          type="button"
          disabled={!inputsValid || busy}
          onClick={() => void run(null)}
        >
          {busy && previewCount === null ? "Checking…" : "Preview recipients"}
        </button>

        {previewCount !== null && (
          <button
            type="button"
            disabled={busy}
            onClick={() => void run(previewCount)}
          >
            {busy
              ? "Sending…"
              : `Send to ${String(previewCount)} member${previewCount === 1 ? "" : "s"}`}
          </button>
        )}
      </div>

      {previewCount !== null && (
        <p>
          <strong>
            {previewCount} member{previewCount === 1 ? "" : "s"}
          </strong>{" "}
          would receive this. Nothing has been sent.
        </p>
      )}

      {sentResult !== null && (
        <p>
          <span className="tag">sent</span> Delivered to {sentResult.sent} of{" "}
          {sentResult.recipient_count}.
          {sentResult.failed > 0 && (
            <>
              {" "}
              {sentResult.failed} failed — those numbers are recorded as hashes
              on <code>outbound_messages</code>, where the delivery status is
              tracked.
            </>
          )}
        </p>
      )}

      {error !== null && (
        <p className="tag tag-error" style={{ display: "inline-block" }}>
          {error}
        </p>
      )}
    </div>
  );
}

export default Newsletter;
